import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createRandomID } from '@/utils/random-id';
import { Badge } from '@/admin/components/ui/badge';
import { Button } from '@/admin/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/admin/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/admin/components/ui/dialog';
import { Input } from '@/admin/components/ui/input';
import { Label } from '@/admin/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/admin/components/ui/table';
import { Textarea } from '@/admin/components/ui/textarea';
import {
  cancelWillowGroupOperation,
  createWillowGroupOperation,
  fetchWillowAgentGroups,
  fetchWillowGroupOperation,
  fetchWillowGroupOperations,
  fetchWillowTemplates,
  previewWillowGroupOperation,
  retryWillowGroupOperationItem,
  type WillowAgentGroup,
  type WillowGroupOperationDetail,
  type WillowGroupOperationItem,
  type WillowGroupOperationJob,
  type WillowGroupOperationPreview,
  type WillowTemplate,
} from '@/admin/lib/api';

const GROUP_PAGE_SIZE = 100;
const OPERATION_PAGE_SIZE = 50;
const ITEM_PAGE_SIZE = 100;
const ALL_ITEM_STATUSES = 'all';

const operationType = 'update_home_agent_template' as const;

const emptySet = new Set<string>();

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function humanize(value?: string | null) {
  if (!value) return '-';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, match => match.toUpperCase());
}

function operationTypeLabel(value?: string | null) {
  switch (value) {
    case operationType:
    case 'set_home_agent_template':
      return 'Update home agent template';
    default:
      return humanize(value);
  }
}

function shortId(value?: string | null) {
  if (!value) return '-';
  if (value.length <= 18) return value;
  return `${value.slice(0, 8)}…${value.slice(-6)}`;
}

function statusVariant(
  status?: string
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'succeeded':
    case 'already_target':
      return 'secondary';
    case 'failed':
    case 'partial_failed':
    case 'expected_failure':
      return 'destructive';
    case 'running':
    case 'will_update':
      return 'default';
    default:
      return 'outline';
  }
}

function createIdempotencyKey() {
  return createRandomID();
}

function selectedGroupsFromSet(
  groups: WillowAgentGroup[],
  selected: Set<string>
) {
  return groups.filter(group => selected.has(group.group_id));
}

function templateOptionLabel(template: WillowTemplate) {
  const id = template.template_id || template.id;
  return `${template.name || id}${template.hidden ? ' (hidden)' : ''}`;
}

export function WillowGroupsPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = React.useState<WillowAgentGroup[]>([]);
  const [templates, setTemplates] = React.useState<WillowTemplate[]>([]);
  const [total, setTotal] = React.useState(0);
  const [query, setQuery] = React.useState('');
  const [searchDraft, setSearchDraft] = React.useState('');
  const [selected, setSelected] = React.useState<Set<string>>(emptySet);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [targetTemplateID, setTargetTemplateID] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [preview, setPreview] =
    React.useState<WillowGroupOperationPreview | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);

  const selectedGroups = React.useMemo(
    () => selectedGroupsFromSet(groups, selected),
    [groups, selected]
  );
  const selectedGroupIDs = React.useMemo(
    () => selectedGroups.map(group => group.group_id),
    [selectedGroups]
  );
  const selectedGroupKey = selectedGroupIDs.join(',');
  const allVisibleSelected =
    groups.length > 0 && groups.every(group => selected.has(group.group_id));

  const loadData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [groupsResponse, templatesResponse] = await Promise.all([
        fetchWillowAgentGroups({ q: query, limit: GROUP_PAGE_SIZE, offset: 0 }),
        fetchWillowTemplates(),
      ]);
      setGroups(groupsResponse.groups);
      setTotal(groupsResponse.total_count);
      setTemplates(templatesResponse.templates);
      setSelected(prev => {
        const visibleIDs = new Set(
          groupsResponse.groups.map(group => group.group_id)
        );
        return new Set([...prev].filter(id => visibleIDs.has(id)));
      });
      setTargetTemplateID(
        current =>
          current ||
          templatesResponse.templates[0]?.template_id ||
          templatesResponse.templates[0]?.id ||
          ''
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load Willow groups'
      );
    } finally {
      setLoading(false);
    }
  }, [query]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  React.useEffect(() => {
    setPreview(null);
    setActionError(null);
  }, [targetTemplateID, reason, selectedGroupKey]);

  const toggleGroup = (groupID: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(groupID)) {
        next.delete(groupID);
      } else {
        next.add(groupID);
      }
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected(() => {
      if (allVisibleSelected) {
        return new Set<string>();
      }
      return new Set(groups.map(group => group.group_id));
    });
  };

  const openOperationDialog = () => {
    setDialogOpen(true);
    setActionError(null);
    setPreview(null);
    setReason('');
    setTargetTemplateID(
      current => current || templates[0]?.template_id || templates[0]?.id || ''
    );
  };

  const buildOperationInput = () => ({
    operation_type: operationType,
    group_ids: selectedGroupIDs,
    params: { target_template_id: targetTemplateID },
    reason: reason.trim(),
  });

  const handlePreview = async () => {
    if (selectedGroupIDs.length === 0 || !targetTemplateID) {
      setActionError('Select groups and a target template first.');
      return;
    }
    setPreviewLoading(true);
    setActionError(null);
    try {
      setPreview(await previewWillowGroupOperation(buildOperationInput()));
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to preview operation'
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!preview) {
      setActionError('Preview the operation before submitting.');
      return;
    }
    if (!reason.trim()) {
      setActionError('Enter an audit reason before submitting.');
      return;
    }
    setSubmitting(true);
    setActionError(null);
    try {
      const job = await createWillowGroupOperation({
        ...buildOperationInput(),
        idempotency_key: createIdempotencyKey(),
      });
      setDialogOpen(false);
      setSelected(new Set());
      navigate(`/willow/operations?job=${encodeURIComponent(job.id)}`);
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to submit operation'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-auto py-4 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-normal">Groups</h1>
            <p className="text-sm text-muted-foreground">
              Query Willow agent groups and safely queue in-place home agent
              template updates while preserving each group&apos;s home agent
              identity and history.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadData}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={openOperationDialog}
              disabled={selectedGroupIDs.length === 0 || templates.length === 0}
            >
              Update home agent template
            </Button>
          </div>
        </div>
      </div>

      <div className="px-4 lg:px-6">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle>Willow groups</CardTitle>
                <CardDescription>
                  Showing {groups.length} of {total} groups. Selected{' '}
                  {selectedGroupIDs.length}.
                </CardDescription>
              </div>
              <form
                className="flex gap-2"
                onSubmit={event => {
                  event.preventDefault();
                  setQuery(searchDraft.trim());
                }}
              >
                <Input
                  className="w-64"
                  placeholder="Search groups, agents, templates"
                  value={searchDraft}
                  onChange={event => setSearchDraft(event.currentTarget.value)}
                />
                <Button type="submit" variant="outline" disabled={loading}>
                  Search
                </Button>
              </form>
            </div>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="rounded-md border border-destructive/40 p-3 text-sm text-destructive">
                {error}
              </div>
            ) : null}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input
                      aria-label="Select all visible groups"
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleAllVisible}
                    />
                  </TableHead>
                  <TableHead>Group</TableHead>
                  <TableHead>Home agent</TableHead>
                  <TableHead>Current template</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="py-8 text-center text-muted-foreground"
                    >
                      Loading groups…
                    </TableCell>
                  </TableRow>
                ) : groups.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="py-8 text-center text-muted-foreground"
                    >
                      No Willow groups found.
                    </TableCell>
                  </TableRow>
                ) : (
                  groups.map(group => (
                    <TableRow
                      key={group.group_id}
                      data-state={
                        selected.has(group.group_id) ? 'selected' : undefined
                      }
                    >
                      <TableCell>
                        <input
                          aria-label={`Select ${group.name || group.group_id}`}
                          type="checkbox"
                          checked={selected.has(group.group_id)}
                          onChange={() => toggleGroup(group.group_id)}
                        />
                      </TableCell>
                      <TableCell className="min-w-56 whitespace-normal">
                        <div className="font-medium">
                          {group.name || group.group_id}
                        </div>
                        <code className="text-xs text-muted-foreground">
                          {group.group_id}
                        </code>
                        {group.purpose ? (
                          <div className="text-xs text-muted-foreground">
                            {group.purpose}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="min-w-48 whitespace-normal">
                        <div>{group.home_agent_name || '-'}</div>
                        <code className="text-xs text-muted-foreground">
                          {group.home_agent_id || '-'}
                        </code>
                      </TableCell>
                      <TableCell className="min-w-52 whitespace-normal">
                        <div>
                          {group.current_template_name ||
                            group.current_template_id ||
                            '-'}
                        </div>
                        {group.current_template_name &&
                        group.current_template_id ? (
                          <code className="text-xs text-muted-foreground">
                            {group.current_template_id}
                          </code>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {group.operation_eligible ? (
                          <Badge variant="secondary">Eligible</Badge>
                        ) : (
                          <Badge variant="outline">
                            {humanize(group.ineligible_reason)}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Update home agent template</DialogTitle>
            <DialogDescription>
              Preview the selected group snapshot before updating each existing
              home agent in place. The operation preserves home_agent_id,
              history, and storage.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-[1fr_1fr]">
            <div className="flex flex-col gap-2">
              <Label htmlFor="willow-target-template">Target template</Label>
              <select
                id="willow-target-template"
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={targetTemplateID}
                onChange={event =>
                  setTargetTemplateID(event.currentTarget.value)
                }
              >
                {templates.map(template => {
                  const value = template.template_id || template.id;
                  return (
                    <option key={value} value={value}>
                      {templateOptionLabel(template)}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="willow-operation-reason">Audit reason</Label>
              <Textarea
                id="willow-operation-reason"
                placeholder="Why this batch change is being made"
                value={reason}
                onChange={event => setReason(event.currentTarget.value)}
              />
            </div>
          </div>

          <div className="rounded-md border p-3 text-sm">
            <div className="font-medium">Selected groups</div>
            <div className="mt-1 text-muted-foreground">
              {selectedGroups
                .slice(0, 6)
                .map(group => group.name || group.group_id)
                .join(', ')}
              {selectedGroups.length > 6
                ? `, +${selectedGroups.length - 6} more`
                : ''}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              This updates the selected groups&apos; existing home agents in
              place. It does not rebind a group to another agent or change the
              Initial Agents defaults for future groups.
            </div>
          </div>

          {actionError ? (
            <div className="rounded-md border border-destructive/40 p-3 text-sm text-destructive">
              {actionError}
            </div>
          ) : null}

          {preview ? (
            <div className="flex flex-col gap-3">
              <div className="grid gap-3 sm:grid-cols-4">
                <PreviewMetric
                  label="Affected"
                  value={preview.affected_count}
                />
                <PreviewMetric
                  label="Already target"
                  value={preview.already_target_count}
                />
                <PreviewMetric
                  label="Expected failures"
                  value={preview.expected_failure_count}
                />
                <PreviewMetric label="Total" value={preview.total_count} />
              </div>
              <p className="text-xs text-muted-foreground">
                Affected items snapshot the expected home agent and current
                template, keep the home agent identity fixed, and skip drifted
                rows instead of force-writing over a newer change.
              </p>
              <div className="max-h-72 overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Group</TableHead>
                      <TableHead>From</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Message</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.items.map(item => (
                      <TableRow key={item.group_id}>
                        <TableCell className="min-w-48 whitespace-normal">
                          <div>{item.group_name || item.group_id}</div>
                          <code className="text-xs text-muted-foreground">
                            {item.group_id}
                          </code>
                        </TableCell>
                        <TableCell className="whitespace-normal">
                          {item.current_template_id || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusVariant(item.preview_status)}>
                            {humanize(item.preview_status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="whitespace-normal text-muted-foreground">
                          {item.message || humanize(item.skipped_reason)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={previewLoading || submitting}
            >
              {previewLoading ? 'Previewing…' : 'Preview'}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!preview || submitting || !reason.trim()}
            >
              {submitting ? 'Submitting…' : 'Confirm operation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PreviewMetric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  );
}

export function WillowOperationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedJobID = searchParams.get('job') || '';
  const [operations, setOperations] = React.useState<WillowGroupOperationJob[]>(
    []
  );
  const [detail, setDetail] = React.useState<WillowGroupOperationDetail | null>(
    null
  );
  const [loadingList, setLoadingList] = React.useState(true);
  const [loadingDetail, setLoadingDetail] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [statusFilter, setStatusFilter] = React.useState(ALL_ITEM_STATUSES);
  const [busyAction, setBusyAction] = React.useState<string | null>(null);

  const loadList = React.useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const response = await fetchWillowGroupOperations({
        limit: OPERATION_PAGE_SIZE,
        offset: 0,
      });
      setOperations(response.operations);
      if (!selectedJobID && response.operations[0]) {
        setSearchParams({ job: response.operations[0].id }, { replace: true });
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load operations'
      );
    } finally {
      setLoadingList(false);
    }
  }, [selectedJobID, setSearchParams]);

  const loadDetail = React.useCallback(async () => {
    if (!selectedJobID) {
      setDetail(null);
      return;
    }
    setLoadingDetail(true);
    setActionError(null);
    try {
      setDetail(
        await fetchWillowGroupOperation(selectedJobID, {
          limit: ITEM_PAGE_SIZE,
          offset: 0,
          status: statusFilter === ALL_ITEM_STATUSES ? undefined : statusFilter,
        })
      );
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to load operation detail'
      );
      setDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  }, [selectedJobID, statusFilter]);

  React.useEffect(() => {
    loadList();
  }, [loadList]);

  React.useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const selectJob = (id: string) => {
    setSearchParams({ job: id });
  };

  const refreshAll = async () => {
    await Promise.all([loadList(), loadDetail()]);
  };

  const handleCancel = async (job: WillowGroupOperationJob) => {
    setBusyAction(`cancel:${job.id}`);
    setActionError(null);
    try {
      await cancelWillowGroupOperation(job.id);
      await refreshAll();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to cancel operation'
      );
    } finally {
      setBusyAction(null);
    }
  };

  const handleRetry = async (item: WillowGroupOperationItem) => {
    setBusyAction(`retry:${item.id}`);
    setActionError(null);
    try {
      await retryWillowGroupOperationItem(item.job_id, item.id);
      await refreshAll();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to retry item'
      );
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-auto py-4 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-normal">
              Operations
            </h1>
            <p className="text-sm text-muted-foreground">
              Track durable Willow group operations, cancel pending work, and
              retry failed items.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={refreshAll}
            disabled={loadingList || loadingDetail}
          >
            Refresh
          </Button>
        </div>
      </div>

      {error ? (
        <div className="px-4 lg:px-6">
          <Card className="border-destructive/40">
            <CardHeader>
              <CardTitle className="text-base">
                Unable to load operations
              </CardTitle>
              <CardDescription>{error}</CardDescription>
            </CardHeader>
          </Card>
        </div>
      ) : null}

      <div className="grid gap-6 px-4 lg:px-6 xl:grid-cols-[0.9fr_1.4fr]">
        <Card>
          <CardHeader>
            <CardTitle>Operation history</CardTitle>
            <CardDescription>
              Most recent {OPERATION_PAGE_SIZE} submitted jobs.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingList ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="py-8 text-center text-muted-foreground"
                    >
                      Loading operations…
                    </TableCell>
                  </TableRow>
                ) : operations.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="py-8 text-center text-muted-foreground"
                    >
                      No Willow operations yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  operations.map(job => (
                    <TableRow
                      key={job.id}
                      data-state={
                        job.id === selectedJobID ? 'selected' : undefined
                      }
                      className="cursor-pointer"
                      onClick={() => selectJob(job.id)}
                    >
                      <TableCell className="min-w-44 whitespace-normal">
                        <div className="font-medium">
                          {operationTypeLabel(job.operation_type)}
                        </div>
                        <code className="text-xs text-muted-foreground">
                          {shortId(job.id)}
                        </code>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(job.status)}>
                          {humanize(job.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {job.succeeded_count +
                          job.skipped_drift_count +
                          job.failed_count +
                          job.canceled_count}
                        /{job.total_count}
                      </TableCell>
                      <TableCell>{formatDateTime(job.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle>Operation detail</CardTitle>
                <CardDescription>
                  {detail
                    ? shortId(detail.job.id)
                    : 'Select an operation to inspect items.'}
                </CardDescription>
              </div>
              {detail ? (
                <div className="flex flex-wrap gap-2">
                  <select
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={statusFilter}
                    onChange={event =>
                      setStatusFilter(event.currentTarget.value)
                    }
                  >
                    <option value={ALL_ITEM_STATUSES}>All item statuses</option>
                    <option value="pending">Pending</option>
                    <option value="running">Running</option>
                    <option value="succeeded">Succeeded</option>
                    <option value="failed">Failed</option>
                    <option value="skipped_drift">Skipped drift</option>
                    <option value="canceled">Canceled</option>
                  </select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCancel(detail.job)}
                    disabled={
                      busyAction === `cancel:${detail.job.id}` ||
                      !['pending', 'running'].includes(detail.job.status)
                    }
                  >
                    {busyAction === `cancel:${detail.job.id}`
                      ? 'Canceling…'
                      : 'Cancel'}
                  </Button>
                </div>
              ) : null}
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {actionError ? (
              <div className="rounded-md border border-destructive/40 p-3 text-sm text-destructive">
                {actionError}
              </div>
            ) : null}

            {!selectedJobID ? (
              <div className="rounded-md border p-6 text-center text-sm text-muted-foreground">
                Select an operation from the history list.
              </div>
            ) : loadingDetail ? (
              <div className="rounded-md border p-6 text-center text-sm text-muted-foreground">
                Loading operation detail…
              </div>
            ) : detail ? (
              <>
                <div className="grid gap-3 sm:grid-cols-5">
                  <PreviewMetric
                    label="Succeeded"
                    value={detail.job.succeeded_count}
                  />
                  <PreviewMetric
                    label="Failed"
                    value={detail.job.failed_count}
                  />
                  <PreviewMetric
                    label="Drift skipped"
                    value={detail.job.skipped_drift_count}
                  />
                  <PreviewMetric
                    label="Canceled"
                    value={detail.job.canceled_count}
                  />
                  <PreviewMetric label="Total" value={detail.job.total_count} />
                </div>
                <div className="rounded-md border p-3 text-sm">
                  <div className="flex flex-wrap gap-2">
                    <Badge variant={statusVariant(detail.job.status)}>
                      {humanize(detail.job.status)}
                    </Badge>
                    <span className="text-muted-foreground">
                      By {detail.job.created_by_email}
                    </span>
                    <span className="text-muted-foreground">
                      Created {formatDateTime(detail.job.created_at)}
                    </span>
                  </div>
                  <div className="mt-2 text-muted-foreground">
                    Reason: {detail.job.reason || '-'}
                  </div>
                </div>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Group</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Expected → Target</TableHead>
                        <TableHead>Attempts</TableHead>
                        <TableHead>Error / reason</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.items.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="py-8 text-center text-muted-foreground"
                          >
                            No items for this filter.
                          </TableCell>
                        </TableRow>
                      ) : (
                        detail.items.map(item => (
                          <TableRow key={item.id}>
                            <TableCell className="min-w-48 whitespace-normal">
                              <div>{item.group_name || item.group_id}</div>
                              <code className="text-xs text-muted-foreground">
                                {item.group_id}
                              </code>
                            </TableCell>
                            <TableCell>
                              <Badge variant={statusVariant(item.status)}>
                                {humanize(item.status)}
                              </Badge>
                            </TableCell>
                            <TableCell className="min-w-56 whitespace-normal">
                              <code className="text-xs text-muted-foreground">
                                {item.expected_current_template_id || '-'} →{' '}
                                {item.target_template_id}
                              </code>
                            </TableCell>
                            <TableCell>{item.attempt}</TableCell>
                            <TableCell className="min-w-56 whitespace-normal text-muted-foreground">
                              {item.last_error || humanize(item.skipped_reason)}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRetry(item)}
                                disabled={
                                  item.status !== 'failed' ||
                                  busyAction === `retry:${item.id}`
                                }
                              >
                                {busyAction === `retry:${item.id}`
                                  ? 'Retrying…'
                                  : 'Retry'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <div className="text-xs text-muted-foreground">
                  Showing {detail.items.length} of {detail.items_total} items
                  for this filter.
                </div>
              </>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
